import React, { useEffect, useState } from 'react';
import {
    MessageSquare, Search, Plus, CheckCircle,
    Clock, ChevronLeft, ChevronRight, Eye
} from 'lucide-react';
import { useCollaborationStore } from '../../store/collaborationStore';
import { ReviewRequest } from '../../services/collaboration.service';
import ReviewCard from './components/ReviewCard';
import ReviewDetailModal from './components/ReviewDetailModal';
import CreateReviewModal from './components/CreateReviewModal';

const CollaborationPage: React.FC = () => {
    const {
        reviews,
        stats,
        isLoading,
        activeTab,
        searchTerm,
        fetchReviews,
        fetchStats,
        setActiveTab,
        setSearchTerm,
        getFilteredReviews,
        deleteReview,
    } = useCollaborationStore();

    // Modal states
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedReview, setSelectedReview] = useState<ReviewRequest | null>(null);

    // Pagination
    const ITEMS_PER_PAGE = 9;
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        fetchReviews();
        fetchStats();
    }, [fetchReviews, fetchStats]);

    // Reset page when tab or search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, searchTerm]);

    const filteredReviews = getFilteredReviews();
    const paginatedReviews = filteredReviews.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );
    const totalPages = Math.ceil(filteredReviews.length / ITEMS_PER_PAGE);

    const handleViewReview = (review: ReviewRequest) => {
        setSelectedReview(review);
        setIsDetailModalOpen(true);
    };

    const handleDeleteReview = async (review: ReviewRequest) => {
        if (window.confirm(`Delete review "${review.title}"?`)) {
            await deleteReview(review.id);
        }
    };

    const handleCreateReview = () => {
        setIsCreateModalOpen(true);
    };

    // Status counts for tabs
    const pendingCount = stats?.pending || 0;
    const inReviewCount = stats?.inReview || 0;
    const changesRequestedCount = stats?.changesRequested || 0;
    const approvedCount = stats?.approved || 0;
    const rejectedCount = stats?.rejected || 0;

    const feedbackCount = pendingCount + inReviewCount + changesRequestedCount;
    const completedCount = approvedCount + rejectedCount;

    // Pagination component
    const PaginationControls = () => {
        if (totalPages <= 1) return null;

        return (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                    Page {currentPage} of {totalPages} ({filteredReviews.length} items)
                </span>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </button>
                    <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </button>
                </div>
            </div>
        );
    };

    if (isLoading && reviews.length === 0) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Internal Feedback</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Review and approve content with your team before client submission</p>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-4 gap-4 w-full max-w-2xl">
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Pending</div>
                        <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-yellow-500" />
                            <span className="text-xl font-bold text-yellow-600">{pendingCount}</span>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">In Review</div>
                        <div className="flex items-center gap-1">
                            <Eye className="w-4 h-4 text-blue-500" />
                            <span className="text-xl font-bold text-blue-600">{inReviewCount}</span>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Approved</div>
                        <div className="flex items-center gap-1">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-xl font-bold text-green-600">{approvedCount}</span>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Comments</div>
                        <div className="flex items-center gap-1">
                            <MessageSquare className="w-4 h-4 text-purple-500" />
                            <span className="text-xl font-bold text-purple-600">{stats?.totalComments || 0}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tab Navigation and Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Status Filter Tabs */}
                    <div className="flex flex-wrap bg-gray-100 dark:bg-gray-700 rounded-xl p-1 gap-1">
                        {[
                            { key: 'feedback', label: 'Active', count: feedbackCount, color: 'yellow' },
                            { key: 'pending', label: 'Pending', count: pendingCount, color: 'yellow' },
                            { key: 'in_review', label: 'In Review', count: inReviewCount, color: 'blue' },
                            { key: 'changes', label: 'Changes', count: changesRequestedCount, color: 'orange' },
                            { key: 'approvals', label: 'Completed', count: completedCount, color: 'green' },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key as any)}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.key
                                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                    }`}
                            >
                                {tab.label}
                                {tab.count > 0 && (
                                    <span className={`px-1.5 py-0.5 text-xs rounded-full ${tab.color === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                                            tab.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                                                tab.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' :
                                                    'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                        }`}>
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Search */}
                    <div className="flex flex-col sm:flex-row gap-4 flex-1 max-w-md">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                            <input
                                type="text"
                                placeholder="Search internal reviews..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
                            />
                        </div>
                    </div>

                    {/* Action Button */}
                    <button
                        onClick={handleCreateReview}
                        className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                        <Plus className="w-4 h-4" />
                        Request Review
                    </button>
                </div>
            </div>

            {/* Content */}
            {filteredReviews.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-12 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-indigo-100 dark:from-primary-900/30 dark:to-indigo-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <MessageSquare className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        {activeTab === 'feedback' ? 'No pending reviews' : 'No completed reviews'}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                        {activeTab === 'feedback'
                            ? 'When tasks are sent for review, they will appear here for feedback.'
                            : 'Completed reviews will be shown here for reference.'}
                    </p>
                    {activeTab === 'feedback' && (
                        <button
                            onClick={handleCreateReview}
                            className="px-6 py-3 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                            Create Review Request
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {paginatedReviews.map((review) => (
                            <ReviewCard
                                key={review.id}
                                review={review}
                                onView={handleViewReview}
                                onDelete={handleDeleteReview}
                            />
                        ))}
                    </div>
                    <PaginationControls />
                </div>
            )}

            {/* Modals */}
            <ReviewDetailModal
                isOpen={isDetailModalOpen}
                onClose={() => {
                    setIsDetailModalOpen(false);
                    setSelectedReview(null);
                }}
                review={selectedReview}
            />

            <CreateReviewModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
            />
        </div>
    );
};

export default CollaborationPage;
